import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { MessageSquare, Mail, Copy, Check, Share2, Download } from "lucide-react";
import { toast } from "sonner";

// Inline branded droplet logo for QR center
const LOGO_DATA_URL = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 80">
  <rect width="64" height="80" rx="14" fill="#FAF7EE"/>
  <path d="M32 14 C 28 22, 24 28, 24 34 C 24 39, 27 42, 32 42 C 37 42, 40 39, 40 34 C 40 28, 36 22, 32 14 Z" fill="#1FB4BC"/>
  <path d="M16 46 C 20 56, 26 60, 32 60 C 38 60, 44 56, 48 46" stroke="#0E5A60" stroke-width="2.4" stroke-linecap="round" fill="none"/>
  <path d="M12 68 Q 20 64, 28 68 T 44 68 T 60 68" stroke="#1FB4BC" stroke-width="2" stroke-linecap="round" fill="none"/>
</svg>
`)}`;

const SHARE_URL = "https://masonmassage.com";
const SHARE_TITLE = "Mason Massage & Wellness";
const SHARE_TEXT = "Mobile massage in Knoxville — booked to your door.";

/**
 * ShareSheet — popup with QR code + Text / Email / Copy / Native share buttons.
 * Works everywhere: iframes, desktop, mobile, in-app browsers.
 */
export default function ShareSheet({ open, onOpenChange }) {
  const qrWrapRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const buildBrandedPng = () => {
    const sourceCanvas = qrWrapRef.current?.querySelector("canvas");
    if (!sourceCanvas) return null;
    const size = 1024;
    const padding = 80;
    const labelArea = 140;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size + labelArea;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FAF7EE";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(sourceCanvas, padding, padding, size - padding * 2, size - padding * 2);
    ctx.fillStyle = "#0E5A60";
    ctx.font = "600 56px Georgia, 'Times New Roman', serif";
    ctx.textAlign = "center";
    ctx.fillText("Mason Massage & Wellness", size / 2, size + 60);
    ctx.fillStyle = "#6b7280";
    ctx.font = "400 32px -apple-system, system-ui, sans-serif";
    ctx.fillText("masonmassage.com", size / 2, size + 110);
    return canvas.toDataURL("image/png");
  };

  const handleText = () => {
    const body = encodeURIComponent(
      `${SHARE_TEXT}\n\nBook here: ${SHARE_URL}`,
    );
    // sms: scheme — opens default Messages app on iOS/Android, opens link on desktop
    window.location.href = `sms:?&body=${body}`;
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(SHARE_TITLE);
    const body = encodeURIComponent(
      `Thought you might like this:\n\n${SHARE_TEXT}\n\n${SHARE_URL}`,
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.message("Copy this link", { description: SHARE_URL });
    }
  };

  const handleNativeShare = async () => {
    const shareData = {
      title: SHARE_TITLE,
      text: SHARE_TEXT,
      url: SHARE_URL,
    };
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        // Try with QR image attached if supported
        const dataUrl = buildBrandedPng();
        if (dataUrl && navigator.canShare) {
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], "mason-massage-qr.png", { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ ...shareData, files: [file] });
            return;
          }
        }
        await navigator.share(shareData);
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    handleCopy();
  };

  const handleSaveQr = () => {
    const dataUrl = buildBrandedPng();
    if (!dataUrl) {
      toast.error("Could not generate QR.");
      return;
    }
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = "mason-massage-qr.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("QR saved to your device");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-sm bg-[#FAF7EE] border-spa-green/10 rounded-3xl p-6"
        data-testid="share-sheet"
      >
        <DialogHeader>
          <DialogTitle className="font-script text-3xl text-spa-dark text-center">
            Share Mason Massage
          </DialogTitle>
          <DialogDescription className="text-center text-spa-dark/60 text-sm">
            Scan the code, or send it via text or email.
          </DialogDescription>
        </DialogHeader>

        {/* QR */}
        <div ref={qrWrapRef} className="flex justify-center my-2">
          <div className="p-3 rounded-2xl bg-white border border-spa-green/10">
            <QRCodeCanvas
              value={SHARE_URL}
              size={180}
              bgColor="#FAF7EE"
              fgColor="#0E5A60"
              level="H"
              imageSettings={{
                src: LOGO_DATA_URL,
                height: 40,
                width: 40,
                excavate: true,
              }}
              data-testid="share-sheet-qr"
            />
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 -mt-1">masonmassage.com</p>

        {/* Share options */}
        <div className="grid grid-cols-4 gap-2 mt-4">
          <ShareOption
            icon={<MessageSquare className="w-5 h-5" />}
            label="Text"
            onClick={handleText}
            testid="share-text"
          />
          <ShareOption
            icon={<Mail className="w-5 h-5" />}
            label="Email"
            onClick={handleEmail}
            testid="share-email"
          />
          <ShareOption
            icon={copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            label={copied ? "Copied" : "Copy"}
            onClick={handleCopy}
            testid="share-copy"
          />
          <ShareOption
            icon={<Share2 className="w-5 h-5" />}
            label="More"
            onClick={handleNativeShare}
            testid="share-more"
          />
        </div>

        <button
          onClick={handleSaveQr}
          data-testid="share-save-qr"
          className="mt-3 w-full flex items-center justify-center gap-2 py-3 rounded-full border border-spa-green/20 text-spa-dark/80 text-sm hover:bg-spa-green/5 transition-colors"
        >
          <Download className="w-4 h-4" />
          Save QR to photos
        </button>
      </DialogContent>
    </Dialog>
  );
}

function ShareOption({ icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex flex-col items-center gap-1.5 py-3 px-1 rounded-2xl bg-white border border-spa-green/10 text-spa-dark hover:border-spa-cyan hover:text-spa-cyan transition-colors"
    >
      {icon}
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}
